import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboardStats,
  fetchLatestComments,
  fetchLatestPosts,
} from "../redux/dashboardSlice";
import { fetchFinancialSummary, fetchMonthlyChart } from "../redux/reportSlice";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import "./css/DashboardPage.css";

const DashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stats, latestComments, latestPosts, loading, error } = useSelector(
    (state) => state.dashboard
  );
  const {
    financialSummary,
    monthlyChart,
    loading: reportsLoading,
    error: reportsError
  } = useSelector(
    (state) => state.reports
  );

  const [period, setPeriod] = useState("month");

  // İlk yüklemede bir kere çalışsın
  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchLatestComments());
    dispatch(fetchLatestPosts());
    dispatch(fetchMonthlyChart(new Date().getFullYear()));
  }, [dispatch]);

  // Period değiştiğinde finansal özet yüklensin
  useEffect(() => {
    dispatch(fetchFinancialSummary({ period }));
  }, [period, dispatch]);

  if (loading || !stats) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (error || reportsError) {
    return (
      <div className="error-container">
        <p className="error-text">Veriler alınamadı: {error || reportsError}</p>
      </div>
    );
  }

  const StatCard = ({ title, value, icon, trend, type = "default" }) => (
    <div className={`stat-card stat-card-${type}`}>
      <div className="stat-card-header">
        <div className={`stat-icon stat-icon-${type}`}>{icon}</div>
        {trend !== undefined && (
          <div
            className={`stat-trend ${trend >= 0 ? "trend-up" : "trend-down"}`}
          >
            <span className="trend-icon">{trend >= 0 ? "📈" : "📉"}</span>
            <span className="trend-value">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="page-title">Dashboard</h1>
          <div className="period-selector">
            <label htmlFor="period">Dönem:</label>
            <select
              id="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="period-select"
              disabled={reportsLoading}
            >
              <option value="today">Bugün</option>
              <option value="week">Bu Hafta</option>
              <option value="month">Bu Ay</option>
              <option value="year">Bu Yıl</option>
            </select>
            {reportsLoading && <span className="loading-indicator">Yükleniyor...</span>}
          </div>
        </div>

        {/* Finansal Özet */}
        {!reportsLoading && financialSummary && (
          <div className="stats-grid">
            <StatCard
              title="Toplam Gelir"
              value={`₺${financialSummary.income?.total?.toFixed(2) || 0}`}
              icon="💰"
              type="success"
            />
            <StatCard
              title="Toplam Gider"
              value={`₺${financialSummary.expenses?.total?.toFixed(2) || 0}`}
              icon="💸"
              type="error"
            />
            <StatCard
              title="Net Kar"
              value={`₺${financialSummary.netProfit?.toFixed(2) || 0}`}
              icon="📊"
              type={financialSummary.netProfit >= 0 ? "primary" : "error"}
            />
            <StatCard
              title="Ödenmemiş"
              value={`₺${financialSummary.income?.unpaid?.toFixed(2) || 0}`}
              icon="⏳"
              type="warning"
            />
          </div>
        )}

        {/* Seans İstatistikleri */}
        {financialSummary?.sessionStats && (
          <div className="stats-grid stats-grid-4">
            <StatCard
              title="Toplam Seans"
              value={financialSummary.sessionStats.total || 0}
              icon="📅"
              type="info"
            />
            <StatCard
              title="Tamamlanan"
              value={financialSummary.sessionStats.completed || 0}
              icon="✅"
              type="success"
            />
            <StatCard
              title="Planlanan"
              value={financialSummary.sessionStats.scheduled || 0}
              icon="🗓️"
              type="primary"
            />
            <StatCard
              title="İptal Edilen"
              value={financialSummary.sessionStats.cancelled || 0}
              icon="❌"
              type="error"
            />
          </div>
        )}

        {/* Aylık Gelir-Gider Grafiği */}
        {monthlyChart?.data && (
          <div className="chart-section">
            <div className="chart-card">
              <h2 className="chart-title">
                Aylık Gelir-Gider Grafiği ({monthlyChart.year})
              </h2>
              <div className="chart-wrapper">
                <BarChart
                  xAxis={[
                    {
                      scaleType: "band",
                      data: monthlyChart.data.map((m) => m.monthName),
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: 13,
                        fontWeight: 500,
                      },
                    },
                  ]}
                  yAxis={[
                    {
                      tickLabelStyle: {
                        fontSize: 13,
                      },
                    },
                  ]}
                  series={[
                    {
                      data: monthlyChart.data.map((m) => m.income),
                      label: "Gelir",
                      color: "#2e7d32",
                      id: "income",
                    },
                    {
                      data: monthlyChart.data.map((m) => m.expense),
                      label: "Gider",
                      color: "#d32f2f",
                      id: "expense",
                    },
                  ]}
                  height={450}
                  margin={{ top: 80, bottom: 100, left: 80, right: 40 }}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "top", horizontal: "middle" },
                      padding: 0,
                    },
                  }}
                  grid={{ horizontal: true }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Kategoriye Göre Giderler ve Seans Tiplerine Göre Gelir */}
        <div className="pie-charts-grid">
          {financialSummary?.expenses?.byCategory &&
            financialSummary.expenses.byCategory.length > 0 && (
              <div className="chart-card">
                <h2 className="chart-title">Kategoriye Göre Giderler</h2>
                <div className="pie-chart-wrapper">
                  <PieChart
                    colors={[
                      "#ef5350",
                      "#ec407a",
                      "#ab47bc",
                      "#7e57c2",
                      "#5c6bc0",
                      "#42a5f5",
                      "#29b6f6",
                      "#26c6da",
                      "#26a69a",
                      "#66bb6a",
                    ]}
                    series={[
                      {
                        data: financialSummary.expenses.byCategory.map(
                          (cat, index) => ({
                            id: index,
                            value: cat.total,
                            label: cat._id,
                          })
                        ),
                        arcLabel: (item) => `₺${item.value.toFixed(0)}`,
                        arcLabelMinAngle: 30,
                        highlightScope: {
                          faded: "global",
                          highlighted: "item",
                        },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: "gray",
                        },
                        innerRadius: 30,
                        outerRadius: 140,
                        paddingAngle: 2,
                        cornerRadius: 5,
                      },
                    ]}
                    width={600}
                    height={400}
                    margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: "vertical",
                        position: { vertical: "middle", horizontal: "end" },
                        padding: 20,
                      },
                    }}
                  />
                </div>
              </div>
            )}

          {financialSummary?.income?.bySessionType &&
            financialSummary.income.bySessionType.length > 0 && (
              <div className="chart-card">
                <h2 className="chart-title">Seans Tiplerine Göre Gelir</h2>
                <div className="pie-chart-wrapper">
                  <PieChart
                    colors={[
                      "#66bb6a",
                      "#26a69a",
                      "#26c6da",
                      "#29b6f6",
                      "#42a5f5",
                      "#5c6bc0",
                      "#7e57c2",
                      "#ab47bc",
                      "#ec407a",
                      "#ef5350",
                    ]}
                    series={[
                      {
                        data: financialSummary.income.bySessionType.map(
                          (type, index) => ({
                            id: index,
                            value: type.total,
                            label: `${type._id} (${type.count})`,
                          })
                        ),
                        arcLabel: (item) => `₺${item.value.toFixed(0)}`,
                        arcLabelMinAngle: 30,
                        highlightScope: {
                          faded: "global",
                          highlighted: "item",
                        },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: "gray",
                        },
                        innerRadius: 30,
                        outerRadius: 140,
                        paddingAngle: 2,
                        cornerRadius: 5,
                      },
                    ]}
                    width={600}
                    height={400}
                    margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                    slotProps={{
                      legend: {
                        direction: "vertical",
                        position: { vertical: "middle", horizontal: "end" },
                        padding: 20,
                      },
                    }}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
